// Stage-2 answer normalization (spec §7.5): deterministic utterance→option
// matching that always runs at ingest, regardless of whether the call engine
// performed in-call (Stage-1) matching. No LLM dependency — canonical values,
// labels, and authored spoken aliases only. Unmatched answers are never
// dropped: they surface as `unlisted` with the verbatim text preserved.

import type {
  ParsedAnswerNormalizationV1,
  TenantAdminOutboundQuestionnaireQuestionV1,
} from "./tenant-admin-outbound-calls-contract.js";
import { parseAnswerNormalizationV1 } from "./tenant-admin-outbound-calls-contract.js";

export interface NormalizedUtteranceV1 {
  match: "canonical" | "unlisted";
  /** Canonical option value when matched; the verbatim text otherwise. */
  value: string;
  verbatim: string;
  confidence: number;
}

function normalizeText(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, " ");
}

const negationLead =
  /(?:\bno\b|\bnot\b|\bnever\b|\bdon['’]?t\b|\bdo not\b|\bdid not\b|\bdidn['’]?t\b|\bdenies\b|\bdeny\b|\bquit\b|\bstopped\b|\bwithout\b)/;

/**
 * True when EVERY occurrence of the alias sits in a negated clause — one
 * un-negated mention is enough to match ("never had a stent, but I do have a
 * stent now" reports the stent). Clause boundaries include conjunctions, not
 * just punctuation, so a transcriber's missing comma cannot flip the result.
 */
function clauseStartBefore(needle: string, index: number): number {
  const marks = [
    needle.lastIndexOf(".", index),
    needle.lastIndexOf(",", index),
    needle.lastIndexOf(";", index),
  ];
  for (const match of needle.slice(0, index).matchAll(/\b(and|but|though|however|although)\b/g)) {
    marks.push(match.index + match[0].length);
  }
  return Math.max(0, ...marks);
}

function aliasIsNegated(needle: string, alias: string): boolean {
  let index = needle.indexOf(alias);
  if (index === -1) return false;

  while (index !== -1) {
    const clause = needle.slice(clauseStartBefore(needle, index), index);
    if (!negationLead.test(clause)) {
      return false;
    }
    index = needle.indexOf(alias, index + alias.length);
  }

  return true;
}

/**
 * Shared yes/no/confirmation utterance parser (call ingest + the upsert
 * transform) — a deterministic token scanner rather than a regex stack: two
 * rounds of regex patching each shipped a silent inversion, and a clinician
 * can audit a token table.
 *
 * Order matters: unclear wins over everything (never fabricate a boolean from
 * an uncertain answer), then the first polarity token decides, with a
 * negation lookbehind so "I have no allergies" is a denial and "yes, not
 * every day" stays an affirmative.
 */
const unsureMarkers = [
  "not sure",
  "unsure",
  "maybe",
  "don't know",
  "dont know",
  "do not know",
  "no idea",
  "can't remember",
  "cant remember",
  "cannot remember",
  "declined",
  "refused",
  "no answer",
];

const affirmativeTokens = ["yes", "yeah", "yep", "yup", "true", "correct", "confirmed", "affirmative"];
const negativeTokens = ["no", "nope", "nah", "never", "none", "false", "negative", "denies", "deny"];
// First-person openers that assert possession/action ("I do smoke").
const firstPersonAffirmative = /^i\s+(do|does|did|have|has|had|am|was)\b/;
// Negators that can follow a first-person opener or precede a token.
const negatorAfterSubject = /^(not|never|no|none)\b/;

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/n't\b/g, " not")
    .split(/[^a-z0-9']+/)
    .filter(Boolean);
}

export function parseYesNoUtteranceV1(text: unknown): boolean | null {
  const raw = String(text ?? "").trim();
  if (!raw) return null;

  const normalized = raw.toLowerCase().replace(/[’']/g, "'").replace(/n't\b/g, " not");
  if (unsureMarkers.some((marker) => normalized.includes(marker))) {
    return null;
  }

  const tokens = tokenize(raw);
  if (tokens.length === 0) return null;

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];

    if (affirmativeTokens.includes(token)) {
      // "confirmed: no medications" / "yes, none" — a negator immediately
      // after the affirmative marker flips it; a later qualifier
      // ("yes, not every day") does not.
      const next = tokens[index + 1];
      if (next && negatorAfterSubject.test(next)) {
        const afterNext = tokens[index + 2];
        const qualifier =
          afterNext && ["every", "more", "less", "than", "often", "always", "all"].includes(afterNext);
        if (!qualifier) return false;
      }
      return true;
    }

    if (negativeTokens.includes(token)) {
      return false;
    }
  }

  // "I have no allergies" / "I do smoke": decide on the first-person opener
  // only after checking for a negator inside the clause it governs.
  if (firstPersonAffirmative.test(normalized)) {
    const rest = normalized.replace(firstPersonAffirmative, "").trim();
    if (negatorAfterSubject.test(rest)) return false;
    return true;
  }

  return null;
}

/**
 * Confirmation-question parser: like parseYesNoUtteranceV1 but distinguishes
 * an explicit "not sure / don't know" from an unparseable utterance, so a
 * voice engine can record `unsure` instead of bouncing the caller through a
 * re-ask loop ("Yes, I am." must land as an affirmative, "no idea" as unsure).
 */
export function parseYesNoUnsureUtteranceV1(
  text: unknown,
): boolean | "unsure" | null {
  const raw = String(text ?? "").trim();
  if (!raw) return null;

  const normalized = raw.toLowerCase().replace(/[’']/g, "'").replace(/n't\b/g, " not");
  if (unsureMarkers.some((marker) => normalized.includes(marker))) {
    return "unsure";
  }

  return parseYesNoUtteranceV1(raw);
}

export function normalizeUtteranceV1(
  utterance: string,
  normalization: ParsedAnswerNormalizationV1,
): NormalizedUtteranceV1 {
  const verbatim = utterance.trim();
  const needle = normalizeText(utterance);

  for (const option of normalization.options) {
    if (
      normalizeText(option.value) === needle ||
      normalizeText(option.label) === needle
    ) {
      return { match: "canonical", value: option.value, verbatim, confidence: 1 };
    }
  }

  for (const option of normalization.options) {
    const aliases = [option.label, ...option.spokenAliases].map(normalizeText);
    if (
      aliases.some(
        (alias) =>
          alias.length > 2 &&
          needle.includes(alias) &&
          !aliasIsNegated(needle, alias),
      )
    ) {
      return {
        match: "canonical",
        value: option.value,
        verbatim,
        confidence: 0.85,
      };
    }
  }

  return { match: "unlisted", value: verbatim, verbatim, confidence: 0 };
}

export interface TenantAdminOutboundUnlistedOptionV1 {
  questionKey: string;
  value: string;
  occurrences: number;
}

/**
 * Scans captured answer sets for values outside a choice question's canonical
 * list — the feed for the admin approval queue [D5]. Free-text questions never
 * produce unlisted entries.
 */
export function collectUnlistedOptionsV1(
  questions: Pick<
    TenantAdminOutboundQuestionnaireQuestionV1,
    "questionKey" | "answerType" | "answerNormalization"
  >[],
  answerSets: Record<string, unknown>[],
): TenantAdminOutboundUnlistedOptionV1[] {
  const found = new Map<string, TenantAdminOutboundUnlistedOptionV1>();

  for (const question of questions) {
    if (
      question.answerType !== "single_choice" &&
      question.answerType !== "multi_choice"
    ) {
      continue;
    }
    const normalization = parseAnswerNormalizationV1(question.answerNormalization);
    if (normalization.options.length === 0) continue;

    for (const answers of answerSets) {
      const raw = answers[question.questionKey];
      if (raw === undefined || raw === null || raw === "") continue;
      const values = Array.isArray(raw) ? raw : [raw];

      for (const value of values) {
        const result = normalizeUtteranceV1(String(value), normalization);
        if (result.match === "canonical") continue;
        const dedupeKey = `${question.questionKey}::${normalizeText(String(value))}`;
        const existing = found.get(dedupeKey);
        if (existing) {
          existing.occurrences += 1;
        } else {
          found.set(dedupeKey, {
            questionKey: question.questionKey,
            value: String(value),
            occurrences: 1,
          });
        }
      }
    }
  }

  return [...found.values()];
}
